import React, { useCallback } from 'react'

import ModalFooterActionContainer from '../TripModal/TripItems/Create/ModalFooterActionContainer'

import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useDeleteDocument } from 'tripPlanner/hooks/api/documents'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

interface Props {
  tripId: string
  documentKey: string
}

function DeleteConfirmation({ tripId, documentKey }: Props) {
  const isMobile = useScreenSizeOnly('mobile')

  const { closeModal } = useModalCallbacks()
  const modalElement = useModalElementContext()

  const close = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])

  const { mutate: deleteDocument } = useDeleteDocument({
    onSuccess: () => {
      showSnackbar('Document deleted successfully', 'success')
    },
    onError: () => {
      showSnackbar('Failed to delete document', 'critical')
    },
    tripId,
  })

  const handleDelete = useCallback(() => {
    deleteDocument({ documentKey })
    close()
  }, [close, deleteDocument, documentKey])

  return (
    <>
      <ModalHeader title="Are you sure you wish to delete the document?" />
      <ModalBody>
        <ModalContent>
          <BodyTextBlock variant="large">
            This will delete the document permanently. If you change your mind,
            you will need to upload the document again.
          </BodyTextBlock>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActionContainer>
          <TextButton
            kind="tertiary"
            size="large"
            fit={`${isMobile ? 'full-width' : 'auto'}`}
            onClick={close}
          >
            Cancel
          </TextButton>
          <TextButton
            kind="primary"
            size="large"
            fit={`${isMobile ? 'full-width' : 'auto'}`}
            onClick={handleDelete}
            type="submit"
          >
            Yes, delete
          </TextButton>
        </ModalFooterActionContainer>
      </ModalFooter>
    </>
  )
}

export default DeleteConfirmation
